import React from "react";
import { useLang } from "../../lib/LanguageContext";
import { JOIN_URL } from "../../lib/content";
import { ArrowUpRight, Sparkles } from "lucide-react";

export default function Hero() {
    const { t, isRTL } = useLang();

    return (
        <section
            id="top"
            className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-24 md:pt-40 md:pb-32"
            data-testid="sau-hero"
        >
            {/* Ambient grid + glow */}
            <div className="absolute inset-0 sau-grid pointer-events-none" aria-hidden />
            <div
                className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[min(900px,130vw)] h-[min(900px,130vw)] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(102, 227, 255, 0.12), rgba(102, 227, 255, 0) 70%)",
                }}
                aria-hidden
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="sau-fade-in">
                        <span
                            className="inline-flex items-center gap-2 sau-badge rounded-full px-3.5 h-8 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.24em]"
                            data-testid="sau-hero-badge"
                        >
                            <span className="sau-badge-dot" />
                            {t.hero.badge}
                        </span>
                    </div>

                    <div className="sau-fade-in sau-fade-in-delay-1 mt-8 sm:mt-12">
                        <h1
                            className="font-bold tracking-tight sau-glow-text break-words"
                            style={{
                                fontSize: "clamp(2.25rem, 7vw, 5.75rem)",
                                lineHeight: 1.02,
                                letterSpacing: "-0.028em",
                            }}
                            data-testid="sau-hero-title"
                        >
                            {t.hero.title}
                        </h1>
                        <div
                            className={`mt-3 sm:mt-4 text-base sm:text-lg md:text-xl text-[color:var(--sau-text-2)] ${
                                isRTL ? "" : "font-arabic"
                            }`}
                            style={{ letterSpacing: 0 }}
                        >
                            {t.hero.titleArabic}
                        </div>
                    </div>

                    <p
                        className="sau-fade-in sau-fade-in-delay-2 mt-7 sm:mt-9 text-[15px] sm:text-base md:text-xl text-[color:var(--sau-text-2)] max-w-3xl mx-auto leading-relaxed"
                        data-testid="sau-hero-supporting"
                    >
                        {t.hero.supporting}
                    </p>

                    <p className="sau-fade-in sau-fade-in-delay-3 mt-4 sm:mt-5 text-[13.5px] sm:text-sm md:text-[15px] text-[color:var(--sau-muted)] max-w-2xl mx-auto leading-relaxed">
                        {t.hero.description}
                    </p>

                    <div className="sau-fade-in sau-fade-in-delay-4 mt-9 sm:mt-11 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                        <a
                            href={JOIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="sau-hero-primary-cta"
                            className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full sau-btn-primary text-sm font-semibold tracking-wide w-full sm:w-auto"
                        >
                            <Sparkles className="w-4 h-4" aria-hidden />
                            {t.hero.primaryCta}
                            <ArrowUpRight
                                className={`w-4 h-4 transition-transform duration-300 ${
                                    isRTL ? "-scale-x-100 group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5"
                                }`}
                                aria-hidden
                            />
                        </a>
                        <a
                            href="#launch"
                            data-testid="sau-hero-secondary-cta"
                            className="inline-flex items-center justify-center h-12 px-6 rounded-full sau-btn-ghost text-sm font-medium tracking-wide w-full sm:w-auto"
                        >
                            {t.hero.secondaryCta}
                        </a>
                    </div>

                    {/* Institutional strip */}
                    <div className="sau-fade-in sau-fade-in-delay-4 mt-14 sm:mt-20 flex items-center justify-center gap-4 sm:gap-6 md:gap-10 text-[9.5px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-[color:var(--sau-muted)]">
                        <span>{t.hero.pillars[0]}</span>
                        <span className="w-1 h-1 rounded-full bg-[color:var(--sau-border)]" />
                        <span>{t.hero.pillars[1]}</span>
                        <span className="w-1 h-1 rounded-full bg-[color:var(--sau-border)]" />
                        <span>{t.hero.pillars[2]}</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
